import { PROVISIONING_STATUS_LABELS, type ProvisioningStatus } from '@bonsai/shared';

// Role taxonomy + visual tokens for the User Management surface.
//
// Roles are an *Auth0* concept (a named bundle of permission strings), not a
// Postgres column — see the "User Management & Permissions" Linear project:
// https://linear.app/bonsai-bar/project/user-management-and-permissions-789cf9a8dda1. This file
// is the single source of truth for how each role is *presented* in the admin
// UI (label, scope, description, chip color). The permission bundles live in
// Auth0 tenant config; we never edit them from here in v1.

export type UserRoleId =
  | 'admin'
  | 'regional-director'
  | 'marketing'
  | 'instructor'
  | 'finance'
  | 'csr';

export type RoleScope = 'global' | 'region' | 'global-readonly';

export type RoleTone = 'bark' | 'moss' | 'water' | 'leaf' | 'gold' | 'clay';

export interface RoleDef {
  id: UserRoleId;
  name: string;
  scope: RoleScope;
  desc: string;
  tone: RoleTone;
}

export const ROLE_DEFS: RoleDef[] = [
  {
    id: 'admin',
    name: 'Admin',
    scope: 'global',
    desc: 'Full read + write across all regions, users, billing, and tenant config.',
    tone: 'bark',
  },
  {
    id: 'regional-director',
    name: 'Regional Director',
    scope: 'region',
    desc: 'Manage staff, venues, workshops, and supplies within assigned region(s).',
    tone: 'moss',
  },
  {
    id: 'marketing',
    name: 'Marketing',
    scope: 'global',
    desc: 'Read across all dashboards; write to marketing copy and venue content.',
    tone: 'water',
  },
  {
    id: 'instructor',
    name: 'Instructor',
    scope: 'region',
    desc: 'Read assigned schedule, supply requests, and resources. No admin surface.',
    tone: 'leaf',
  },
  {
    id: 'finance',
    name: 'Finance / C-Suite',
    scope: 'global-readonly',
    desc: 'Read-only access across every region and every dashboard. No writes.',
    tone: 'gold',
  },
  {
    id: 'csr',
    name: 'CSR',
    scope: 'global',
    desc: 'Customer service: find customers and bookings in every region, create and refund bookings.',
    tone: 'clay',
  },
];

export const ROLE_BY_ID: Record<string, RoleDef> = Object.fromEntries(
  ROLE_DEFS.map((r) => [r.id, r]),
);

/** Tailwind class triplet (text / bg / border) per chip tone. The palette
 *  matches frontend/tailwind.config.js (moss/bark/stone/clay) plus the
 *  browse-design leaf/gold/water values used on the customer widget. */
export const ROLE_TONE_CLASSES: Record<RoleTone, string> = {
  bark: 'text-[#173404] bg-[#EAF3DE] border-stone-300',
  moss: 'text-moss-700 bg-moss-50 border-moss-200',
  water: 'text-[#1d64d7] bg-[#eff8ff] border-[#bfe3fe]',
  leaf: 'text-[#2D5A1B] bg-[#E6F2DA] border-[#97C459]',
  gold: 'text-[#BA7517] bg-[#FAECD8] border-[#FAC775]',
  clay: 'text-clay-700 bg-clay-50 border-clay-200',
};

/** Selected-state class triplet for the role pickers (Invite drawer, Edit
 *  role modal). Unlike `ROLE_TONE_CLASSES` — which paints a self-contained
 *  chip — the picker cards nest their own `dark:` text overrides, so the
 *  selected tint has to invert in dark mode: a deep tinted background that a
 *  light foreground reads against. Without the dark half the card keeps its
 *  pale tint while the labels go near-white, which is unreadable. */
export const ROLE_TONE_SELECTED_CLASSES: Record<RoleTone, string> = {
  bark: 'text-[#173404] bg-[#EAF3DE] border-stone-300 dark:text-[#CDE3B4] dark:bg-[#26331B] dark:border-[#3E5230]',
  moss: 'text-moss-700 bg-moss-50 border-moss-200 dark:text-moss-200 dark:bg-[#0d2b1c] dark:border-moss-700',
  water:
    'text-[#1d64d7] bg-[#eff8ff] border-[#bfe3fe] dark:text-water-200 dark:bg-[#132a44] dark:border-water-700',
  leaf: 'text-[#2D5A1B] bg-[#E6F2DA] border-[#97C459] dark:text-[#C6E0AC] dark:bg-[#20301A] dark:border-[#4A6B31]',
  gold: 'text-[#BA7517] bg-[#FAECD8] border-[#FAC775] dark:text-[#F0C97D] dark:bg-[#33260F] dark:border-[#7A5A22]',
  clay: 'text-clay-700 bg-clay-50 border-clay-200 dark:text-clay-200 dark:bg-[#33200F] dark:border-clay-800',
};

export const SCOPE_LABEL: Record<RoleScope, string> = {
  global: 'Global',
  region: 'Region-scoped',
  'global-readonly': 'Global · read only',
};

// ── User status ────────────────────────────────────────────────────────────
// `inactive` when the row is deactivated, otherwise its provisioning status.
export type UserStatus = ProvisioningStatus | 'inactive';

export interface StatusDef {
  label: string;
  /** text + bg Tailwind classes */
  className: string;
  dot: string;
}

export const STATUS_DEFS: Record<UserStatus, StatusDef> = {
  active: {
    label: PROVISIONING_STATUS_LABELS.active,
    className: 'text-moss-700 bg-moss-50',
    dot: 'bg-moss-700',
  },
  invited: {
    label: PROVISIONING_STATUS_LABELS.invited,
    className: 'text-[#1d64d7] bg-[#eff8ff]',
    dot: 'bg-[#1d64d7]',
  },
  inactive: { label: 'Inactive', className: 'text-stone-600 bg-stone-100', dot: 'bg-stone-600' },
  imported: {
    label: PROVISIONING_STATUS_LABELS.imported,
    className: 'text-[#BA7517] bg-[#FAECD8]',
    dot: 'bg-[#BA7517]',
  },
};

/** Display order for the status filter. */
export const STATUS_FILTER_ORDER: UserStatus[] = ['active', 'invited', 'imported', 'inactive'];

// ── Deterministic avatar color (mirrors the design's umColorFor) ─────────────
const AVATAR_COLORS = [
  '#0e874b',
  '#cc6726',
  '#257bea',
  '#ba7517',
  '#7A5AE0',
  '#1e51ae',
  '#993C1D',
  '#5d675d',
  '#0c6b3d',
  '#3b99f5',
];

export function avatarColorFor(seed: string): string {
  const sum = (seed || '').split('').reduce((a, c) => a + c.charCodeAt(0), 0);
  return AVATAR_COLORS[sum % AVATAR_COLORS.length];
}
